import type { BaseEntity } from "../../UniversalCRMTable/types/shared";
import type { DetailHeaderModel } from "./model";
import {
  DEAL_HEADER_FIELDS,
  DEAL_HEADER_EDITABLE_FIELDS,
  buildDealHeaderModel,
  type DealHeaderData,
} from "./entities/deals";
import {
  CONTACT_HEADER_FIELDS,
  CONTACT_HEADER_EDITABLE_FIELDS,
  buildContactHeaderModel,
  type ContactHeaderData,
} from "./entities/contacts";
import {
  COMPANY_HEADER_FIELDS,
  COMPANY_HEADER_EDITABLE_FIELDS,
  buildCompanyHeaderModel,
  type CompanyHeaderData,
} from "./entities/companies";
import {
  TICKET_HEADER_FIELDS,
  TICKET_HEADER_EDITABLE_FIELDS,
  buildTicketHeaderModel,
  type TicketHeaderData,
} from "./entities/tickets";
import {
  CONTRACT_HEADER_FIELDS,
  CONTRACT_HEADER_EDITABLE_FIELDS,
  buildContractHeaderModel,
  type ContractHeaderData,
} from "./entities/contracts";
import {
  TIME_ENTRY_HEADER_FIELDS,
  TIME_ENTRY_HEADER_EDITABLE_FIELDS,
  buildTimeEntryHeaderModel,
  type TimeEntryHeaderData,
} from "./entities/time-entries";
import {
  TASK_HEADER_FIELDS,
  TASK_HEADER_EDITABLE_FIELDS,
  buildTaskHeaderModel,
  type TaskHeaderData,
} from "./entities/tasks";
import {
  COMMUNICATION_HEADER_FIELDS,
  COMMUNICATION_HEADER_EDITABLE_FIELDS,
  buildCommunicationHeaderModel,
  type CommunicationHeaderData,
} from "./entities/communications";
import {
  RECURRING_CHARGE_HEADER_FIELDS,
  RECURRING_CHARGE_HEADER_EDITABLE_FIELDS,
  buildRecurringChargeHeaderModel,
  type RecurringChargeHeaderData,
} from "./entities/recurring-charges";
import {
  RECURRING_TRANSACTION_HEADER_FIELDS,
  RECURRING_TRANSACTION_HEADER_EDITABLE_FIELDS,
  buildRecurringTransactionHeaderModel,
  type RecurringTransactionHeaderData,
} from "./entities/recurring-transactions";
import {
  OFFERING_HEADER_FIELDS,
  OFFERING_HEADER_EDITABLE_FIELDS,
  buildOfferingHeaderModel,
  type OfferingHeaderData,
} from "./entities/offerings";
import {
  EMPLOYEE_HEADER_FIELDS,
  EMPLOYEE_HEADER_EDITABLE_FIELDS,
  buildEmployeeHeaderModel,
  type EmployeeHeaderData,
} from "./entities/employees";

/**
 * Registry of typed header-model builders keyed by entity-registry key.
 *
 * Builders are typed against `Pick<>`s of the codegen GraphQL types; the
 * registry is the one type-erased boundary (the same pattern as
 * `lib/entity-registry` — the universal layer dispatches by string key, the
 * concrete modules stay codegen-typed). The runtime record handed to the
 * detail header IS the entity's detail-query node, so the conversion at this
 * boundary is sound as long as `*_HEADER_FIELDS` ⊆ the detail query selection
 * — which `tests/utils/detail-header-model-coverage.test.ts` enforces.
 */
interface HeaderModelEntry {
  fields: readonly string[];
  /** 1:1 scalar columns the header edits in place (dropped from the inline panel). */
  editableFields: readonly string[];
  build: (item: BaseEntity) => DetailHeaderModel;
}

const HEADER_MODEL_REGISTRY: Record<string, HeaderModelEntry> = {
  deals: {
    fields: DEAL_HEADER_FIELDS,
    editableFields: DEAL_HEADER_EDITABLE_FIELDS,
    build: (item) => buildDealHeaderModel(item as unknown as DealHeaderData),
  },
  contacts: {
    fields: CONTACT_HEADER_FIELDS,
    editableFields: CONTACT_HEADER_EDITABLE_FIELDS,
    build: (item) => buildContactHeaderModel(item as unknown as ContactHeaderData),
  },
  companies: {
    fields: COMPANY_HEADER_FIELDS,
    editableFields: COMPANY_HEADER_EDITABLE_FIELDS,
    build: (item) => buildCompanyHeaderModel(item as unknown as CompanyHeaderData),
  },
  tickets: {
    fields: TICKET_HEADER_FIELDS,
    editableFields: TICKET_HEADER_EDITABLE_FIELDS,
    build: (item) => buildTicketHeaderModel(item as unknown as TicketHeaderData),
  },
  contracts: {
    fields: CONTRACT_HEADER_FIELDS,
    editableFields: CONTRACT_HEADER_EDITABLE_FIELDS,
    build: (item) => buildContractHeaderModel(item as unknown as ContractHeaderData),
  },
  time_entries: {
    fields: TIME_ENTRY_HEADER_FIELDS,
    editableFields: TIME_ENTRY_HEADER_EDITABLE_FIELDS,
    build: (item) => buildTimeEntryHeaderModel(item as unknown as TimeEntryHeaderData),
  },
  tasks: {
    fields: TASK_HEADER_FIELDS,
    editableFields: TASK_HEADER_EDITABLE_FIELDS,
    build: (item) => buildTaskHeaderModel(item as unknown as TaskHeaderData),
  },
  communications: {
    fields: COMMUNICATION_HEADER_FIELDS,
    editableFields: COMMUNICATION_HEADER_EDITABLE_FIELDS,
    build: (item) => buildCommunicationHeaderModel(item as unknown as CommunicationHeaderData),
  },
  recurring_charges: {
    fields: RECURRING_CHARGE_HEADER_FIELDS,
    editableFields: RECURRING_CHARGE_HEADER_EDITABLE_FIELDS,
    build: (item) => buildRecurringChargeHeaderModel(item as unknown as RecurringChargeHeaderData),
  },
  recurring_transactions: {
    fields: RECURRING_TRANSACTION_HEADER_FIELDS,
    editableFields: RECURRING_TRANSACTION_HEADER_EDITABLE_FIELDS,
    build: (item) =>
      buildRecurringTransactionHeaderModel(item as unknown as RecurringTransactionHeaderData),
  },
  offerings: {
    fields: OFFERING_HEADER_FIELDS,
    editableFields: OFFERING_HEADER_EDITABLE_FIELDS,
    build: (item) => buildOfferingHeaderModel(item as unknown as OfferingHeaderData),
  },
  employees: {
    fields: EMPLOYEE_HEADER_FIELDS,
    editableFields: EMPLOYEE_HEADER_EDITABLE_FIELDS,
    build: (item) => buildEmployeeHeaderModel(item as unknown as EmployeeHeaderData),
  },
};

/** Build the typed header model for an entity, or null when unregistered. */
export function getDetailHeaderModel(entity: string, item: BaseEntity): DetailHeaderModel | null {
  const entry = HEADER_MODEL_REGISTRY[entity];
  if (!entry) return null;
  return entry.build(item);
}

/**
 * Item fields the entity's header model reads — used by memo equality so the
 * header re-renders when (and only when) a consumed field changes.
 */
export function getDetailHeaderModelFields(entity: string): readonly string[] {
  return HEADER_MODEL_REGISTRY[entity]?.fields ?? [];
}

/** Entity keys with a registered header-model builder (test access). */
export function getHeaderModelEntityKeys(): string[] {
  return Object.keys(HEADER_MODEL_REGISTRY);
}

/**
 * Fields the entity's header edits IN PLACE (1:1 scalar columns). These are
 * dropped from the inline edit panel so each field has exactly one editor, and
 * the panel/header coverage guard treats them as "placed in the header".
 * Empty array for unregistered entities.
 */
export function getHeaderEditableFields(entity: string): readonly string[] {
  return HEADER_MODEL_REGISTRY[entity]?.editableFields ?? [];
}
